// AGC: MainMenu - Main app shell that stays always on
// AGC: NOW WITH: Hamburger + Left Drawer Navigation (replaces TopNav)
// AGC: Renders hamburger, dynamic center content, AppDrawer, and BottomNav
// AGC: Now includes voice recording integration

import React, { useState, useCallback, useEffect, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags, APP_VERSION } from '@droidfarm/shared'
import toast from 'react-hot-toast'  // AGC: Toast notifications for voice confirmations

// AGC: Import all page components
import { Home } from './Home'
import { 
  FieldsList, 
  FieldDetail, 
  NewPlotStep1, 
  NewPlotStep2, 
  PlotSummary,
  // AGC: New field creation flow components
  GeometryMethodSelection,
  GPSPathGeometry,
  ManualPolygonGeometry,
  CropSuggestions,
  FieldTaskPlanning,
  // AGC: Lotes-specific components
  LotesList,
  LoteDetail,
  PlotImagesPage,
  PolygonDrawingMap,
  NovoLote,
  CreateWithGPS,
  AIOneClickLot,
  AutoDetectFields,
  FinalizarLote,
  // AGC: Task management flow
  SelectCrop,
  ManagementPlan,
  // AGC: TaskDetailScreen REMOVED - Use TaskDetail from tasks module
  // AGC: Lot Page components
  LotPage,
  // AGC: Farm Lots Map View
  FarmLotsMapView,
} from './fields'
import { TasksList, NewTask, TaskDetail } from './tasks'
import { WeatherDashboard, SpraySuitability, Forecast } from './weather'
import { Profile } from './profile'
import { Notifications } from './notifications'
import { Despesas, NewExpense, FinancesPage, NewRevenue } from './financial'
import { BottomNav } from './shared'  // AGC: TopNav removed from imports
import {
  VoiceRecordingModal,
  VoiceCommandHelpModal,
  shouldShowVoiceHelp,
  setVoiceHelpPreference,
} from './voice'
import { VoiceRecordingBar } from './voice/VoiceRecordingBar'
import type { VoiceHelpPreference } from './voice'
import { SubscriptionPage } from './subscription'
import { AppDrawer } from './layout/AppDrawer'  // AGC: Drawer navigation
import { AppHeader } from './layout/AppHeader'  // AGC: NEW - Global header
import { useIsDesktopLayout } from '../hooks/useIsDesktopLayout'  // AGC: Desktop shell detection
import DesktopSidebar from './shared/DesktopSidebar'  // AGC: Desktop sidebar shell
import { getDesktopWidthClass } from './shared/desktopPageWidths'  // AGC: Per-page content width registry
import { 
  AdvisorMarketplace, 
  ConsultantProfile, 
  ConsultationRequest, 
  ConsultationsList,
  ConsultationDetail,
} from './advisors'
import { UnifiedInventoryPage } from './inventory'
// AGC: NEW - Machinery and Team pages
import { MachinesList, AddMachine, MachineDetail } from './machinery'
import { Settings } from './settings/Settings'
import { SistemaSettings } from './settings/SistemaSettings'
import { CategoriasSettings } from './settings/CategoriasSettings'
import { TarefasSettings } from './settings/TarefasSettings'
import { MaquinarioSettings } from './settings/MaquinarioSettings'
import { CulturasSettings } from './settings/CulturasSettings'
import { VoiceCalibration } from './voice/VoiceCalibration'
import { TeamUsers, AddTeamMember } from './team'

// AGC: Import voice recording hook, hybrid voice hook, auth hook, translation, and permissions
import { useVoiceRecording, useAuth, useNotifications, useTranslation, usePermissions } from '../hooks'
import { useRBAC } from '../hooks/useRBAC'
import { getBackendUrl } from '../utils/backendUrl'

// AGC: Import voice context for queue count
import { useVoiceQueueCount, useVoiceOnlineStatus, useVoiceMode } from '../context'

// AGC: Import context
import { useLotes } from '../context'

// AGC: Import types
import type { Screen, NavigationState, NavTab, ActivePage, PlotFormData } from '../types'

// AGC: Import team components
import { MemberProfile } from './team/MemberProfile'

/**
 * AGC: MainMenu - Main container component
 * AGC: This component is ALWAYS mounted and renders:
 * AGC: - Top section (user header/nav - currently part of Home)
 * AGC: - Center section (dynamic page content)
 * AGC: - Bottom section (BottomNav)
 * AGC: - Voice recording modal (overlay)
 */
// AGC: Screens that have been migrated to React Router — navigate via URL, not internal state
const ROUTED_SCREENS: Record<string, string> = {
  'dashboard': '/',
  'profile': '/profile',
  'notifications': '/notifications',
  'subscription': '/subscription',
  'lotes-list': '/lotes',
  'fields': '/lotes',
  'novo-lote': '/lotes/new',
  'create-with-gps': '/lotes/new/gps',
  'ai-one-click-lot': '/lotes/new/ai',
  'auto-detect-fields': '/lotes/new/auto-detect',
  'finalizar-lote': '/lotes/finalize',
  'polygon-map': '/lotes/polygon',
  'farm-lots-map': '/lotes/map',
  'crop-suggestions': '/lotes/crop-suggestions',
  'field-task-planning': '/lotes/task-planning',
  'lot-page': '/lotes/view',
  'tasks': '/tasks',
  'new-task': '/tasks/new',
  'task-detail': '/tasks/view',
  'lot-task-detail': '/tasks/view',
  'weather': '/weather',
  'spray-suitability': '/weather/spray',
  'forecast': '/weather/forecast',
  'finances': '/finances',
  'expenses': '/expenses',
  'new-expense': '/expenses/new',
  'new-revenue': '/revenues/new',
  'settings': '/settings',
  'inventory': '/inventory',
  'inputs': '/inventory',
  'machinery': '/machinery',
  'machine-detail': '/machinery/view',
  'team-users': '/team',
}

export const MainMenu: React.FC<{ contentOnly?: boolean; initialPage?: ActivePage; initialNavContext?: Record<string, any> }> = ({ contentOnly = false, initialPage, initialNavContext }) => {
  const { isBaseAppContext } = useFeatureFlags()
  const { addPlot } = useLotes()
  const routerNavigate = useNavigate()

  // AGC: Active page state - tracks which page to show in center
  // AGC: When initialPage is provided (from LegacyMainMenuAdapter), use it as default
  const [activePage, setActivePage] = useState<ActivePage>(initialPage || 'dashboard')

  // AGC: Sync activePage when initialPage changes (URL-driven navigation from LegacyMainMenuAdapter)
  useEffect(() => {
    if (initialPage && initialPage !== activePage) {
      setActivePage(initialPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPage])
  
  // AGC: Guard against double-submission of voice entries (e.g., user double-clicks Confirm)
  const isSubmittingRef = useRef(false)

  // AGC: Active bottom nav tab
  const [activeNav, setActiveNav] = useState<NavTab>('dashboard')
  
  // AGC: NEW - Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  
  // AGC: Navigation context data (for field/task details)
  const [navContext, setNavContext] = useState<{
    selectedFieldId?: string
    selectedPlotId?: string
    selectedTaskId?: string
    plotData?: PlotFormData
    previousPage?: ActivePage
    plotId?: string
    fieldId?: string
    lotName?: string
    cropType?: string
    cropName?: string
    task?: any
    preselectedPlotId?: string  // AGC: For pre-selecting plot when creating task from lot page
    farmId?: string
    // AGC: For finalizar-lote after AI detection
    coordinates?: Array<{ lat: number; lng: number }>
    metrics?: {
      areaHectares?: number
      areaSquareMeters?: number
      perimeterMeters?: number
      perimeterKilometers?: number
      centroid?: { lat: number; lng: number }
      boundingBox?: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } }
    }
    detectionMethod?: string
    // AGC: For member-profile
    memberId?: string
    // AGC: For voice-prefilled field creation
    voicePrefillArea?: number
    voicePrefillCrop?: string
    // AGC: For camera scan modal auto-open (voice command trigger)
    autoOpenScanModal?: boolean
  }>(initialNavContext || {})

  // AGC: Voice recording state
  const [isVoiceModalVisible, setIsVoiceModalVisible] = useState(false)
  const [recordingMode, setRecordingMode] = useState<'idle' | 'tap' | 'hold'>('idle')
  // AGC: First-time / weekly voice command help overlay (shown before voice modal when applicable)
  const [showVoiceHelpModal, setShowVoiceHelpModal] = useState(false)
  
  // AGC: Accumulated field values for voice command confirmation
  // AGC: Tracks values entered by user for each field before final creation
  const [accumulatedFieldValues, setAccumulatedFieldValues] = useState<Record<string, string | number>>({})

  // AGC: Hybrid voice system context (for queue count and online status)
  // AGC: Always available here — MainMenu renders inside VoiceProvider
  const voiceQueueCount = useVoiceQueueCount()
  const isVoiceOnline = useVoiceOnlineStatus()
  const { mode: voiceMode } = useVoiceMode()

  // AGC: Get real user data from Firebase Auth - all data from Firebase, no hardcoded fallbacks
  // AGC: CRITICAL - This must be before useVoiceRecording so we can pass userId
  const { user, getAuthToken } = useAuth()
  
  // AGC: Get user permissions for role-based access control
  const permissions = usePermissions()
  const rbac = useRBAC()
  
  // AGC: Get user's preferred language for voice transcription and translation function
  const { language, t } = useTranslation()

  // AGC: Desktop shell detection — true on real desktop browsers, never on Capacitor native
  const isDesktopLayout = useIsDesktopLayout()

  // AGC: Handle voice command results
  const handleVoiceResult = useCallback(async (response: any) => {
    console.log('🎤 AGC: Processing voice result:', response.entryType)
    
    if (response.entryType === 'field' && response.entry) {
      // AGC: Field was created in Firebase, refresh plots to get it
      console.log('✅ AGC: Field created in Firebase:', response.entry.name)
      
      // AGC: Refresh plots from Firebase to get the newly created field/plot
      try {
        // AGC: The field is already in Firebase with a plot inside
        // AGC: Force a refresh of the plots list to show it
        // AGC: This triggers LotesContext to fetch from /api/v1/fields
        window.dispatchEvent(new CustomEvent('refresh-plots'))
      } catch (error) {
        console.error('AGC: Error refreshing plots:', error)
      }
      
      // AGC: Navigate to fields list to show the new plot
      setActivePage('lotes-list')
      setActiveNav('fields')
    }

    // AGC: scan_product intent -- navigate to inventory with auto-open camera modal
    if (response.entryType === 'scan_product' || response.action === 'open_camera_scan') {
      console.log('📸 AGC: Scan product voice command detected, opening camera modal')
      setIsVoiceModalVisible(false)
      setNavContext(prev => ({ ...prev, autoOpenScanModal: true }))
      setActivePage('inventory')
      setActiveNav('dashboard')
      toast.success(t('productDetection.openingCamera', 'Opening camera to detect product...'))
    }
  }, [t])

  // AGC: Initialize voice recording hook with userId from auth
  const {
    state: voiceState,
    isRecording,
    duration,
    progress,
    error: voiceError,
    lastResponse,
    startRecording,
    stopRecording,
    cancelRecording,
    reset: resetVoice,
    isPaused,
    analyserNode,
    pauseRecording,
    resumeRecording,
  } = useVoiceRecording({
    maxDuration: 60, // AGC: 60 seconds max
    userId: user?.uid || null, // AGC: Pass userId for entry association
    language: language, // AGC: User's preferred language for Whisper transcription
    onRecordingStart: () => {
      console.log('🎤 AGC: Voice recording started')
    },
    onRecordingStop: (result) => {
      console.log('🎤 AGC: Voice recording stopped', result.duration, 'seconds')
    },
    onProcessingComplete: (response) => {
      console.log('🎤 AGC: Voice processing complete:', response)
      if (response.success) {
        // AGC: Handle different entry types
        handleVoiceResult(response)
      }
    },
    onError: (error) => {
      console.error('🎤 AGC: Voice error:', error)
    },
  })
  
  // AGC: Extract user name and avatar from auth context (from Firebase only)
  const userName = user?.displayName || user?.profile?.displayName || ''
  const userAvatar = user?.photoURL || user?.profile?.photoURL || ''

  // AGC: Get notifications and unread count - synced with Notifications page
  const { unreadCount } = useNotifications()

  // AGC: Log app context on mount
  useEffect(() => {
    console.log('🌾 AGC: DroidFarm MainMenu initialized')
    console.log(`📱 AGC: Context: ${isBaseAppContext ? 'BaseApp' : 'Desktop'}`)
  }, [isBaseAppContext])

  // AGC: Map page to nav tab
  const pageToNav = useCallback((page: ActivePage): NavTab => {
    if (page === 'dashboard') return 'dashboard'
    // AGC: All field-related and lotes pages map to 'fields' nav
    if (
      page.startsWith('field') || 
      page.startsWith('new-plot') || 
      page === 'plot-summary' || 
      page === 'crop-suggestions' ||
      page.startsWith('geometry') ||
      page.startsWith('lotes') ||
      page === 'plot-detail' ||
      page === 'polygon-map' ||
      page === 'novo-lote' ||
      page === 'create-with-gps' ||
      page === 'select-crop' ||
      page === 'management-plan' ||
      page === 'lot-task-detail' ||
      page === 'lot-page' ||
      page === 'lot-images' ||
      page === 'farm-lots-map'
    ) return 'fields'
    if (page === 'finances') return 'finances'
    if (page === 'inventory' || page === 'inputs') return 'inventory'
    if (page.startsWith('task') || page === 'new-task') return 'tasks'
    if (page === 'weather' || page === 'spray-suitability' || page === 'forecast') return 'weather'
    return 'dashboard'
  }, [])

  // AGC: Navigate to a page — routes migrated screens through React Router, legacy through internal state
  const navigateToPage = useCallback((page: ActivePage, data?: Partial<typeof navContext>) => {
    const routePath = ROUTED_SCREENS[page]
    if (routePath && !contentOnly) {
      // AGC: Migrated screen — navigate via React Router URL with state
      routerNavigate(routePath, { state: data || undefined })
      window.scrollTo({ top: 0, behavior: 'instant' })
      return
    }
    // AGC: Legacy screen — internal state machine
    setNavContext(prev => ({
      ...prev,
      previousPage: activePage,
      ...(data || {}),
    }))
    setActivePage(page)
    setActiveNav(pageToNav(page))

    // AGC: Scroll to top on navigation
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [activePage, pageToNav, routerNavigate, contentOnly])

  // AGC: Handle Screen navigation (for compatibility with existing components)
  const handleScreenNavigate = useCallback((screen: Screen, data?: Partial<NavigationState>) => {
    // AGC: Map Screen to ActivePage (they use the same values)
    navigateToPage(screen as ActivePage, data)
  }, [navigateToPage])

  // AGC: Go back to previous page
  const goBack = useCallback(() => {
    const previousPage = navContext.previousPage || 'dashboard'
    setNavContext(prev => ({
      ...prev,
      previousPage: undefined,
    }))
    setActivePage(previousPage)
    setActiveNav(pageToNav(previousPage))
  }, [navContext.previousPage, pageToNav])

  // AGC: Handle bottom nav click
  const handleNavClick = useCallback((navId: NavTab) => {
    setActiveNav(navId)
    
    // AGC: Map nav to page - fields nav now goes to lotes-list
    const navToPage: Record<NavTab, ActivePage> = {
      'dashboard': 'dashboard',
      'fields': 'lotes-list',
      'finances': 'finances',
      'inventory': 'inventory',
      'tasks': 'tasks',
      'weather': 'weather',
    }
    
    navigateToPage(navToPage[navId])
  }, [navigateToPage])

  // AGC: Handle mic tap (short press) - starts inline recording bar in tap mode
  const handleMicPress = useCallback(() => {
    if (shouldShowVoiceHelp()) {
      setShowVoiceHelpModal(true)
      return
    }
    setRecordingMode('tap')
    startRecording()
  }, [startRecording])

  // AGC: Handle mic long-press - starts inline recording bar in hold mode
  const handleMicLongPress = useCallback(() => {
    if (shouldShowVoiceHelp()) {
      setShowVoiceHelpModal(true)
      return
    }
    setRecordingMode('hold')
    startRecording()
  }, [startRecording])

  // AGC: Bar/hold handlers removed — MainMenu only renders in contentOnly mode now.
  // AGC: MainLayout owns the recording bar, BottomNav, and all mic gesture handlers.

  // AGC: User chose "Don't show again" or "Show once a week" on voice help
  const handleVoiceHelpPreference = useCallback(
    (preference: VoiceHelpPreference) => {
      setVoiceHelpPreference({
        preference,
        ...(preference === 'weekly' ? { lastShownAt: new Date().toISOString() } : {}),
      })
      setShowVoiceHelpModal(false)
      // AGC: Start in tap mode after help dismissal
      setRecordingMode('tap')
      startRecording()
    },
    [startRecording]
  )

  // AGC: Close voice help without choosing (e.g. X) - still open voice so they can speak
  const handleVoiceHelpClose = useCallback(() => {
    setShowVoiceHelpModal(false)
    setRecordingMode('tap')
    startRecording()
  }, [startRecording])

  // AGC: Handle voice modal close
  const handleVoiceModalClose = useCallback(() => {
    setIsVoiceModalVisible(false)
    resetVoice()
    setAccumulatedFieldValues({})
    // AGC: Refresh all data pages — Farm Agent creates entries server-side
    window.dispatchEvent(new CustomEvent('task-created'))
    window.dispatchEvent(new CustomEvent('refresh-machines'))
    window.dispatchEvent(new CustomEvent('refresh-finances'))
    window.dispatchEvent(new CustomEvent('refresh-inventory'))
    window.dispatchEvent(new CustomEvent('refresh-fields'))
    window.dispatchEvent(new CustomEvent('refresh-plots'))
  }, [resetVoice])

  // AGC: Handle stop recording
  const handleStopRecording = useCallback(() => {
    stopRecording()
  }, [stopRecording])

  // AGC: Handle cancel recording
  const handleCancelRecording = useCallback(() => {
    cancelRecording()
    setIsVoiceModalVisible(false)
  }, [cancelRecording])

  // AGC: Handle field suggestion selection
  // AGC: Called when user selects a suggestion for an unrecognized field value
  // AGC: Accumulates field values and only creates entry when ALL required fields are filled
  const handleFieldSuggestionSelect = useCallback(async (fieldName: string, selectedValue: string, label: string) => {
    console.log(`🎤 AGC: Field suggestion selected: ${fieldName} = ${selectedValue} (${label})`)
    console.log(`📋 AGC: Entry type: ${lastResponse?.entryType}`)
    
    if (!lastResponse?.parsedData || !lastResponse?.fieldSuggestions) {
      console.warn('⚠️ AGC: No parsed data or field suggestions available')
      return
    }
    
    // AGC: Accumulate field values - merge with previous selections
    const newAccumulatedValues = {
      ...accumulatedFieldValues,
      [fieldName]: selectedValue,
    }
    setAccumulatedFieldValues(newAccumulatedValues)
    console.log('📝 AGC: Accumulated field values:', newAccumulatedValues)
    
    // AGC: Merge accumulated values with original parsed data
    const updatedData = {
      ...lastResponse.parsedData,
      ...newAccumulatedValues,
    }
    console.log('📝 AGC: Updated data:', updatedData)
    
    // AGC: For TASK, only category and dueDate are required (same as handleConfirmFields)
    // AGC: Other entry types use all non-optional fields from fieldSuggestions
    const fieldSuggestions = lastResponse.fieldSuggestions
    const requiredFields = lastResponse.entryType === 'task'
      ? ['category', 'dueDate']
      : Object.entries(fieldSuggestions)
          .filter(([_, info]) => !info.optional)
          .map(([key]) => key)

    // AGC: Check which required fields are still missing
    const missingFields = requiredFields.filter(field => {
      const v = updatedData[field]
      const hasInData = v !== undefined && v !== null && v !== '' && String(v).toLowerCase() !== 'undefined'
      const hasAccumulated = newAccumulatedValues[field] !== undefined
      return !hasInData && !hasAccumulated
    })
    
    console.log(`📋 AGC: Required fields: ${requiredFields.join(', ')}`)
    console.log(`📋 AGC: Missing fields: ${missingFields.length > 0 ? missingFields.join(', ') : 'NONE - ready to create!'}`)
    
    // AGC: If there are still missing required fields, don't create yet
    if (missingFields.length > 0) {
      console.log('⏳ AGC: Waiting for more fields to be filled...')
      return
    }
    
    // AGC: All required fields are filled - proceed with creation
    console.log('✅ AGC: All required fields filled, creating entry...')

    const backendUrl = getBackendUrl()
    
    // AGC: Handle FIELD entry type - navigate to novo-lote instead of creating directly
    // AGC: This allows user to choose boundary creation method (AI/GPS/Draw/Auto)
    if (lastResponse.entryType === 'field' && user?.uid) {
      console.log('🗺️ AGC: Field detected via voice (legacy path), navigating to novo-lote...')
      console.log('📋 AGC: Pre-filled data:', { name: updatedData.name, area: updatedData.area, cropType: updatedData.cropType })
      
      // AGC: Reset accumulated values
      setAccumulatedFieldValues({})
      
      // AGC: Close the voice modal first
      handleVoiceModalClose()
      
      // AGC: Navigate to novo-lote with pre-filled data from voice (include farm if chosen in modal)
      navigateToPage('novo-lote', {
        lotName: String(updatedData.name || ''),
        selectedFieldId: updatedData.selectedFieldId ?? updatedData.fieldId,
        fieldId: updatedData.selectedFieldId ?? updatedData.fieldId,
        voicePrefillArea: updatedData.area != null ? Number(updatedData.area) : undefined,
        voicePrefillCrop: updatedData.cropType ? String(updatedData.cropType) : undefined,
      })
      
      toast.success(t('voice.fieldDetected', 'Talhão detectado! Escolha como criar os limites.'))
      return
    }
    
    // AGC: Handle VEHICLE entry type
    if ((lastResponse.entryType === 'vehicle' || lastResponse.entryType === 'vehicle:create') && user?.uid) {
      try {
        console.log('🚜 AGC: Creating vehicle with data:', updatedData)
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType: 'vehicle:create',
            data: {
              name: String(updatedData.name || 'Novo veículo'),
              category: updatedData.category || updatedData.type || 'outros',
              brand: updatedData.brand || '',
              model: updatedData.model || '',
              year: updatedData.year ? Number(updatedData.year) : null,
              horimeterHours: updatedData.horimeterHours ? Number(updatedData.horimeterHours) : 0,
              width: updatedData.width ? Number(updatedData.width) : null,
              // AGC: Map Portuguese status to English (GPT returns Portuguese)
              status: (() => {
                const statusMap: Record<string, string> = {
                  'disponivel': 'available',
                  'disponível': 'available',
                  'em-uso': 'in_use',
                  'em uso': 'in_use',
                  'manutencao': 'maintenance',
                  'manutenção': 'maintenance',
                  'inativo': 'maintenance',
                }
                const rawStatus = String(updatedData.status || '').toLowerCase()
                return statusMap[rawStatus] || 'available'
              })(),
            },
            transcription: lastResponse.transcription,
            userId: user.uid,
            matchId: lastResponse.matchId, // AGC: SOTA voice logging
            userEdits: undefined, // AGC: ML correction learning (legacy path — no diff available)
          }),
        })
        
        if (response.ok) {
          const result = await response.json()
          console.log('✅ AGC: Vehicle created successfully:', result)
          // AGC: Show success toast
          const vehicleName = result.name || updatedData.name || 'Veículo'
          toast.success(`✅ ${vehicleName} criado com sucesso!`, {
            duration: 3000,
            icon: '🚜',
          })
          // AGC: Refresh machines list
          window.dispatchEvent(new CustomEvent('refresh-machines'))
        } else {
          const errorText = await response.text()
          console.error('AGC: Failed to create vehicle:', errorText)
          toast.error('Erro ao criar veículo. Tente novamente.')
        }
      } catch (error) {
        console.error('AGC: Error creating vehicle:', error)
      }
      
      // AGC: Reset accumulated values
      setAccumulatedFieldValues({})
      
      // AGC: Navigate to machines list
      setActivePage('machines')
      setActiveNav('fields') // AGC: Machines is under fields nav
      
      // AGC: Close the modal
      handleVoiceModalClose()
    }
  }, [lastResponse, user?.uid, handleVoiceModalClose, accumulatedFieldValues])

  // AGC: Handle Confirm with all fields merged (avoids async state race from per-field callbacks)
  const handleConfirmFields = useCallback(async (mergedData: Record<string, string | number | null>) => {
    if (!lastResponse?.parsedData || !user?.uid) return
    // AGC: Prevent double submission — if already submitting, ignore the second click
    if (isSubmittingRef.current) {
      console.warn('⚠️ AGC: handleConfirmFields — already submitting, ignoring duplicate call')
      return
    }
    isSubmittingRef.current = true
    const entryType = lastResponse.entryType
    // #region agent log
    // #endregion
    const backendUrl = getBackendUrl()
    const data = { ...lastResponse.parsedData, ...mergedData }

    // AGC: Compute userEdits — diff between SOTA engine suggestion and user's final values.
    // AGC: This powers the correction learning loop: corrections are saved to Firestore
    // AGC: and used by matchScorer.computeCorrectionBias to boost corrected values next time.
    const userEdits: Record<string, { from: unknown; to: unknown }> = {}
    const originalParsed = lastResponse.parsedData || {}
    for (const key of Object.keys(data)) {
      // AGC: Skip internal/metadata keys
      if (key.startsWith('_') || key === 'fieldSuggestions' || key === 'navigateToTaskCreation') continue
      const origVal = originalParsed[key]
      const newVal = data[key]
      // AGC: Only record if both exist and are different
      if (newVal !== null && newVal !== undefined && newVal !== '' && origVal !== newVal) {
        userEdits[key] = { from: origVal ?? null, to: newVal }
      }
    }
    if (Object.keys(userEdits).length > 0) {
      console.log('📊 AGC: [ML] userEdits computed:', JSON.stringify(userEdits))
    }

    // AGC: Handle FIELD entry type - navigate to novo-lote instead of creating directly
    // AGC: This allows user to choose boundary creation method (AI/GPS/Draw/Auto)
    if (entryType === 'field') {
      console.log('🗺️ AGC: Field detected via voice, navigating to novo-lote...')
      console.log('📋 AGC: Pre-filled data:', { name: data.name, area: data.area, cropType: data.cropType })
      
      // AGC: Close the voice modal first
      handleVoiceModalClose()
      
      // AGC: Navigate to novo-lote with pre-filled data from voice (include farm if chosen in modal)
      navigateToPage('novo-lote', {
        lotName: String(data.name || ''),
        selectedFieldId: data.selectedFieldId ?? data.fieldId,
        fieldId: data.selectedFieldId ?? data.fieldId,
        voicePrefillArea: data.area != null ? Number(data.area) : undefined,
        voicePrefillCrop: data.cropType ? String(data.cropType) : undefined,
      })

      toast.success(t('voice.fieldDetected', 'Talhão detectado! Escolha como criar os limites.'))
      isSubmittingRef.current = false
      return
    }

    // AGC: Handle TASK entry type - create task via API or navigate to new-task page
    if (entryType === 'task') {
      // AGC: Use effective data — merge from fieldSuggestions when form shows pre-selected but data is empty
      const fs = lastResponse?.fieldSuggestions || {}
      let effectiveData = { ...data }
      for (const key of ['category', 'dueDate']) {
        const v = effectiveData[key]
        const isEmpty = v === undefined || v === null || v === '' || v === 'none' || String(v).toLowerCase() === 'undefined'
        if (isEmpty && fs[key] && typeof fs[key] === 'object') {
          const info = fs[key] as { value?: string; originalValue?: string }
          const fromFs = info.value ?? info.originalValue
          if (fromFs) {
            effectiveData = { ...effectiveData, [key]: fromFs }
          }
        }
      }
      // AGC: Default fallbacks — allow creation with sensible defaults instead of blocking
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      const effectiveCategory = effectiveData.category && String(effectiveData.category).toLowerCase() !== 'undefined'
        ? String(effectiveData.category)
        : (fs.category && typeof fs.category === 'object' && (fs.category as { value?: string }).value) || 'outros'
      const effectiveDueDate = effectiveData.dueDate && String(effectiveData.dueDate).toLowerCase() !== 'undefined' && String(effectiveData.dueDate).length >= 10
        ? String(effectiveData.dueDate)
        : (fs.dueDate && typeof fs.dueDate === 'object' && (fs.dueDate as { value?: string }).value) || tomorrow.toISOString().split('T')[0]

      effectiveData = { ...effectiveData, category: effectiveCategory, dueDate: effectiveDueDate }
      const dataForTask = effectiveData

      // AGC: Final validation — only block if we truly have nothing (should not happen with fallbacks)
      const missingTaskFields = ['category', 'dueDate'].filter((f) => {
        const v = dataForTask[f]
        return !v || String(v).trim() === '' || String(v).toLowerCase() === 'undefined'
      })
      if (missingTaskFields.length > 0) {
        toast.error(t('voice.missingFields', 'Preencha todos os campos obrigatórios.'))
        isSubmittingRef.current = false
        return
      }

      // AGC: Handle "create new field" selection - navigate to field creation first
      if (data.field === '__new__') {
        console.log('🗺️ AGC: User wants to create new field for task, navigating to novo-lote...')
        handleVoiceModalClose()
        // AGC: Store task data in navContext to resume after field creation
        navigateToPage('novo-lote', {
          previousPage: activePage,
        })
        toast(t('voice.createFieldFirst', 'Crie o campo primeiro, depois retorne para criar a tarefa.'), { icon: '🗺️' })
        isSubmittingRef.current = false
        return
      }

      // AGC: Map priority from internal key to task format
      const priorityMap: Record<string, string> = { alta: 'high', media: 'medium', baixa: 'low' }
      const taskPriority = priorityMap[String(data.priority || 'media')] || String(data.priority || 'medium')

      // AGC: Resolve assignee - handle self-reference phrases from voice ("eu mesmo", "myself", etc.)
      const SELF_PHRASES = ['self', 'eu', 'eu mesmo', 'eu mesma', 'myself', 'yo', 'yo mismo', 'yo misma', 'me']
      const rawAssigneeValue = String(data.assignee || 'self').toLowerCase().trim()
      const isSelfAssignment = SELF_PHRASES.includes(rawAssigneeValue)
      const assigneeName = isSelfAssignment
        ? (user.displayName || user.email || 'Eu')
        : String(data.assigneeName || data.assignee || 'Não atribuído')
      const resolvedAssigneeId = isSelfAssignment ? user.uid : String(data.assignee || '')

      console.log(`👤 AGC: Resolved assignee: "${assigneeName}" (ID: ${resolvedAssigneeId}, isSelf: ${isSelfAssignment})`)

      // AGC: Resolve field - handle voice-extracted field text vs UI-selected field ID
      let taskFieldId = ''
      let taskFieldName = String(data.fieldName || '')
      if (data.field && data.field !== '__new__') {
        // AGC: Check if the field value is a valid Firestore ID (from UI selector) or raw text (from voice)
        const fieldSuggestions = lastResponse?.fieldSuggestions?.field?.suggestions || []
        const matchedField = fieldSuggestions.find(
          (s: { value: string; label: string }) => s.value === data.field || s.label.toLowerCase() === String(data.field).toLowerCase()
        )
        if (matchedField) {
          taskFieldId = matchedField.value
          taskFieldName = matchedField.label
          console.log(`✅ AGC: Field matched from suggestions: "${data.field}" → "${taskFieldName}" (ID: ${taskFieldId})`)
        } else {
          // AGC: Voice-extracted field name doesn't match any registered field
          // AGC: Try fuzzy matching against field suggestions
          const voiceField = String(data.field).toLowerCase().trim()
          const fuzzyMatch = fieldSuggestions.find(
            (s: { value: string; label: string }) => s.label.toLowerCase().includes(voiceField) || voiceField.includes(s.label.toLowerCase())
          )
          if (fuzzyMatch) {
            taskFieldId = fuzzyMatch.value
            taskFieldName = fuzzyMatch.label
            console.log(`🔍 AGC: Field fuzzy-matched: "${data.field}" → "${taskFieldName}" (ID: ${taskFieldId})`)
          } else {
            // AGC: No match found - use fieldId if provided separately, otherwise leave empty
            taskFieldId = data.fieldId ? String(data.fieldId) : ''
            taskFieldName = String(data.field || data.fieldName || '')
            console.log(`⚠️ AGC: Field "${data.field}" not matched to any registered field`)
          }
        }
      } else if (data.fieldId) {
        taskFieldId = String(data.fieldId)
      }

      // AGC: Resolve machinery (optional or required depending on category)
      // AGC: Handle both single machinery (legacy) and multi-select (new)
      let selectedMachineryArray: Array<{ id: string; name: string; category: string }> = []
      
      // AGC: First check for multi-select machinery (new format)
      if (data.selectedMachinery && data.selectedMachinery !== 'none') {
        try {
          const parsed = JSON.parse(String(data.selectedMachinery))
          if (Array.isArray(parsed) && parsed.length > 0) {
            selectedMachineryArray = parsed
            console.log(`🚜 AGC: Multi-select machinery: ${selectedMachineryArray.length} machines`)
          }
        } catch { /* ignore parse errors */ }
      }
      
      // AGC: Fallback to single machinery (legacy format)
      if (selectedMachineryArray.length === 0 && data.machinery && data.machinery !== 'none') {
        selectedMachineryArray = [{
          id: String(data.machinery),
          name: String(data.machineryName || ''),
          category: '',
        }]
      }

      // AGC: Parse selected input products (stored as JSON string in inputValues)
      let selectedInputs: Array<{
        inputId: string
        inputName: string
        totalQuantity: number
        quantityPerHa: number
        unit: string
        category: string
      }> = []
      try {
        if (data.selectedInputs) {
          selectedInputs = JSON.parse(String(data.selectedInputs))
          console.log(`📦 AGC: Task has ${selectedInputs.length} input products selected`)
        }
      } catch {
        console.warn('⚠️ AGC: Could not parse selectedInputs')
      }

      try {
        // AGC: Use the voice create-entry endpoint (same as vehicle flow, no auth header needed)
        const taskBody = {
          title: String(dataForTask.title || data.title || 'Tarefa'),
          description: String(dataForTask.description || data.description || ''),
          category: String(dataForTask.category || 'outros'),
          priority: taskPriority,
          status: 'pending',
          progress: 0,
          dueDate: String(dataForTask.dueDate || new Date().toISOString()),
          fieldId: taskFieldId,
          fieldName: taskFieldName,
          assignedTo: assigneeName,
          assignedToId: resolvedAssigneeId,
          machinery: selectedMachineryArray,
          inputs: selectedInputs,
        }
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType: 'task',
            data: taskBody,
            transcription: lastResponse?.transcription || '',
            userId: user.uid,
            matchId: lastResponse?.matchId, // AGC: SOTA voice logging
            userEdits: Object.keys(userEdits).length > 0 ? userEdits : undefined, // AGC: ML correction learning
          }),
        })
        if (response.ok) {
          toast.success(`✅ Tarefa "${data.title || 'Tarefa'}" criada!`, { duration: 3000, icon: '📋' })
          window.dispatchEvent(new CustomEvent('task-created'))
        } else {
          const errText = await response.text()
          console.error('AGC: Failed to create task:', errText)
          toast.error('Erro ao criar tarefa. Tente novamente.')
        }
      } catch (err) {
        console.error('AGC: Error creating task:', err)
        toast.error('Erro ao criar tarefa. Tente novamente.')
      }
      setAccumulatedFieldValues({})
      setActivePage('tasks')
      setActiveNav('fields')
      handleVoiceModalClose()
      isSubmittingRef.current = false
      return
    }

    if (entryType === 'vehicle' || entryType === 'vehicle:create') {
      // AGC: Build required fields based on category
      // AGC: Width required for: colheitadeira, plantadeira, pulverizador, distribuidor
      // AGC: Tank capacity required for: pulverizador
      const vehicleCat = String(data.category || data.type || '')
      const categoriesRequiringWidth = ['colheitadeira', 'plantadeira', 'pulverizador', 'distribuidor']
      const required = ['category', 'horimeterHours', 'year']
      if (categoriesRequiringWidth.includes(vehicleCat)) required.push('width')
      if (vehicleCat === 'pulverizador') required.push('tankCapacity')
      const missing = required.filter((f) => {
        const v = data[f]
        return v === undefined || v === null || v === ''
      })
      if (missing.length > 0) {
        console.warn('⚠️ AGC: Missing required vehicle fields:', missing)
        toast.error(t('voice.missingFields', 'Preencha todos os campos obrigatórios.'))
        isSubmittingRef.current = false
        return
      }

      // AGC: Build proper vehicle name from category + brand
      const categoryLabelsMap: Record<string, string> = {
        trator: 'Trator', colheitadeira: 'Colheitadeira', plantadeira: 'Plantadeira',
        pulverizador: 'Pulverizador', distribuidor: 'Distribuidor', implemento: 'Implemento',
        irrigacao: 'Irrigação', transporte: 'Transporte', outros: 'Outros',
      }
      const vehicleCategory = vehicleCat || 'outros'
      const vehicleBrand = String(data.brand || '')
      // AGC: Rebuild name as "Category Brand" when brand is selected
      const vehicleName = vehicleBrand
        ? `${categoryLabelsMap[vehicleCategory] || vehicleCategory} ${vehicleBrand}`.trim()
        : String(data.name || 'Novo veículo')

      try {
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType: 'vehicle:create',
            data: {
              name: vehicleName,
              category: vehicleCategory,
              brand: vehicleBrand,
              model: data.model || '',
              year: data.year != null ? Number(data.year) : null,
              horimeterHours: data.horimeterHours != null ? Number(data.horimeterHours) : 0,
              width: data.width != null ? Number(data.width) : null, // AGC: Backend maps to specs.workingWidthMeters
              tankCapacity: data.tankCapacity != null ? Number(data.tankCapacity) : null, // AGC: Backend maps to specs.tankCapacityLiters
              status: (() => {
                const statusMap: Record<string, string> = {
                  disponivel: 'available',
                  'disponível': 'available',
                  'em-uso': 'in_use',
                  'em uso': 'in_use',
                  manutencao: 'maintenance',
                  manutenção: 'maintenance',
                  inativo: 'maintenance',
                }
                return statusMap[String(data.status || '').toLowerCase()] || 'available'
              })(),
            },
            transcription: lastResponse.transcription,
            userId: user.uid,
            matchId: lastResponse.matchId, // AGC: SOTA voice logging
            userEdits: Object.keys(userEdits).length > 0 ? userEdits : undefined, // AGC: ML correction learning
          }),
        })
        if (response.ok) {
          const result = await response.json()
          const createdName = result.name || vehicleName || 'Veículo'
          toast.success(`✅ ${createdName} criado com sucesso!`, { duration: 3000, icon: '🚜' })
          window.dispatchEvent(new CustomEvent('refresh-machines'))

          // AGC: If brand was custom (not in known suggestions), save as learned value
          // AGC: This adds new brands to voice values for future recognition
          if (vehicleBrand && lastResponse?.fieldSuggestions?.brand?.isBrandSelector) {
            const knownBrands = lastResponse.fieldSuggestions.brand.suggestions || []
            const isKnownBrand = knownBrands.some((s) => s.value === vehicleBrand)
            if (!isKnownBrand) {
              const brandCategoryKey = (lastResponse.fieldSuggestions.brand as any).brandCategory
              if (brandCategoryKey) {
                try {
                  await fetch(`${backendUrl}/api/v1/patterns/learned-values`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      fieldType: brandCategoryKey,
                      value: vehicleBrand.toLowerCase(),
                      label: vehicleBrand,
                      aliases: [vehicleBrand.toLowerCase().replace(/[\s\-_]/g, '')],
                    }),
                  })
                  console.log(`📚 AGC: Saved new brand "${vehicleBrand}" to voice values (${brandCategoryKey})`)
                } catch (learnErr) {
                  console.warn('⚠️ AGC: Could not save new brand to voice values:', learnErr)
                }
              }
            }
          }
        } else {
          const errText = await response.text()
          console.error('AGC: Failed to create vehicle:', errText)
          toast.error('Erro ao criar veículo. Tente novamente.')
        }
      } catch (err) {
        console.error('AGC: Error creating vehicle:', err)
        toast.error('Erro ao criar veículo. Tente novamente.')
      }
      setAccumulatedFieldValues({})
      setActivePage('machines')
      setActiveNav('fields')
      handleVoiceModalClose()
    }

    // AGC: Handle REVENUE entry type — create revenue via API
    if (entryType === 'revenue' || entryType === 'expense') {
      console.log(`💰 AGC: ${entryType} detected via voice, creating entry...`)

      // AGC: Price can come from either data.value (buildLegacyData) or data.quantity (preModal extraction / inputValues)
      // AGC: quantity is the slot name, value is the legacy key — check both to avoid losing preModal-dictated prices
      const revenueValue = data.quantity != null && Number(data.quantity) > 0
        ? Number(data.quantity)
        : (data.value != null ? Number(data.value) : 0)
      const revenueTitle = String(data.title || (entryType === 'revenue' ? 'Receita via voz' : 'Despesa via voz'))
      const revenueCategory = String(data.category || 'other')

      // AGC: VALIDATION — Price is REQUIRED for all financial entries
      if (!revenueValue || revenueValue <= 0) {
        console.warn(`⚠️ AGC: ${entryType} blocked — value is required but got: ${revenueValue}`)
        toast.error(t('financial.priceRequired', 'Valor é obrigatório. Informe o preço.'), { duration: 4000 })
        isSubmittingRef.current = false
        return
      }

      // AGC: Parse selected machinery for revenue/expense entries
      // AGC: For machinery_sale category (revenue), selected machines will be marked as sold
      // AGC: For machinery_purchase category (expense), a new machine will be auto-created
      let financialSelectedMachinery: Array<{ id: string; name: string; category: string }> = []
      if (data.selectedMachinery && data.selectedMachinery !== 'none') {
        try {
          const parsed = JSON.parse(String(data.selectedMachinery))
          if (Array.isArray(parsed) && parsed.length > 0) {
            financialSelectedMachinery = parsed
            console.log(`🚜 AGC: ${entryType} has ${financialSelectedMachinery.length} machine(s) selected`)
          }
        } catch { /* ignore parse errors */ }
      }

      // AGC: Parse selected inputs for revenue/expense entries
      // AGC: For grain_sale (revenue), stock will be deducted
      // AGC: For inputs purchase (expense), stock will be added
      let financialSelectedInputs: string | undefined
      if (data.selectedInputs) {
        financialSelectedInputs = String(data.selectedInputs)
        console.log(`📦 AGC: ${entryType} has selectedInputs`)
      }

      try {
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType,
            data: {
              // AGC: Pass ALL parsedData fields through — backend schema validates.
              // AGC: This prevents the "field extracted but never sent" bug class (e.g. tankName).
              ...data,
              // AGC: Override computed/coerced values that need explicit handling
              title: revenueTitle,
              value: revenueValue,
              category: revenueCategory,
              date: data.date || new Date().toISOString(),
              notes: String(data.notes || ''),
              // AGC: Physical quantity needs Number() coercion
              itemQuantity: data.itemQuantity != null ? Number(data.itemQuantity) : undefined,
              // AGC: Selected machinery/inputs need conditional JSON serialization
              selectedMachinery: financialSelectedMachinery.length > 0
                ? JSON.stringify(financialSelectedMachinery) : undefined,
              selectedInputs: financialSelectedInputs,
              // AGC: Conditional fields
              soldFieldId: revenueCategory === 'land_sale' ? (data.fieldId || data.field) : undefined,
              financialTreatment: data.financialTreatment || undefined,
              skipInventorySync: data.skipInventorySync === 1 || data.skipInventorySync === true ? true : undefined,
            },
            transcription: lastResponse?.transcription || '',
            userId: user.uid,
            matchId: lastResponse?.matchId,
            userEdits: Object.keys(userEdits).length > 0 ? userEdits : undefined,
          }),
        })
        if (response.ok) {
          const entryResult = await response.json()
          const icon = entryType === 'revenue' ? '💵' : '💰'
          const label = entryType === 'revenue' ? 'Receita' : 'Despesa'
          toast.success(`${icon} ${label} "${revenueTitle}" registrada!`, { duration: 3000 })
          // AGC: Show asset lifecycle notifications
          if (revenueCategory === 'machinery_sale' && financialSelectedMachinery.length > 0) {
            const soldNames = financialSelectedMachinery.map(m => m.name).join(', ')
            toast.success(`🚜 ${financialSelectedMachinery.length > 1 ? 'Máquinas removidas' : 'Máquina removida'} do inventário: ${soldNames}`, { duration: 4000 })
          }
          if (revenueCategory === 'machinery_purchase') {
            toast.success(`🚜 Nova máquina adicionada ao inventário: ${revenueTitle}`, { duration: 4000 })
          }
          if (revenueCategory === 'land_sale' && data.fieldId) {
            toast.success(`🌾 Talhão removido do cadastro após venda`, { duration: 4000 })
          }
          // AGC: Show inventory sync feedback toast based on backend sync result
          const sync = entryResult?.inventorySync || entryResult?.entry?.inventorySync
          if (sync) {
            switch (sync.action) {
              case 'stock_added':
                toast.success(`📦 ${sync.itemName || 'Item'} adicionado ao estoque (total: ${sync.newQty ?? '?'})`, { duration: 4000 })
                break
              case 'item_created':
                toast.success(`📦 Novo item "${sync.itemName || 'Item'}" adicionado ao inventário`, { duration: 4000 })
                break
              case 'routed_to_tank':
                toast.success(`⛽ ${sync.fuelName || 'Combustível'} adicionado ao ${sync.tankLabel || 'tanque'} (${sync.fillPct ?? '?'}%)`, { duration: 4000 })
                break
              case 'tank_created_and_filled':
                toast.success(`⛽ Tanque "${sync.tankLabel || '?'}" criado e abastecido (${sync.fillPct ?? '?'}%)`, { duration: 5000 })
                break
              case 'needs_tank_confirm':
                toast(`⚠️ Tanque "${sync.tankName || '?'}" não encontrado. Configure em Armazéns.`, { duration: 6000, icon: '⛽' })
                break
              case 'routed_to_container': {
                const ctype = sync.containerType === 'silo' ? 'Silo' : 'Tanque'
                const emoji = sync.containerType === 'silo' ? '🌾' : '⛽'
                toast.success(`${emoji} ${sync.itemName || 'Item'} adicionado ao ${ctype} "${sync.containerLabel || '?'}" (${sync.fillPct ?? '?'}%)`, { duration: 4000 })
                break
              }
              case 'container_created_and_filled': {
                const ctype = sync.containerType === 'silo' ? 'Silo' : 'Tanque'
                const emoji = sync.containerType === 'silo' ? '🌾' : '⛽'
                toast.success(`${emoji} ${ctype} "${sync.containerLabel || '?'}" criado e abastecido com ${sync.itemName || 'item'} (${sync.fillPct ?? '?'}%)`, { duration: 5000 })
                break
              }
              case 'needs_container_confirm':
                toast(`⚠️ Container "${sync.containerName || '?'}" não encontrado. Configure em Armazéns ou diga o nome exato.`, { duration: 6000, icon: '📦' })
                break
              case 'skipped':
                // AGC: Show detailed warnings for non-silent skip reasons (product mismatch, stale assignment)
                if (sync.detail === 'container_product_mismatch' || sync.detail === 'container_stale_assignment') {
                  toast.error(sync.error || `⚠️ Container indisponível para esta operação`, { duration: 7000 })
                }
                break
              case 'stock_deducted':
                toast.success(`📤 ${sync.deducted ?? '?'}x ${sync.itemName || 'Item'} deduzido do estoque${sync.partial ? ' (parcial)' : ''}`, { duration: 4000 })
                break
              case 'machine_archived':
                toast.success(`🚜 ${sync.itemName || 'Máquina'} marcada como vendida`, { duration: 4000 })
                break
              case 'field_archived':
                toast.success(`🌾 ${sync.itemName || 'Talhão'} marcado como vendido`, { duration: 4000 })
                break
              case 'entry_recorded':
                toast.success(`📥 Entrada: ${sync.quantity ?? '?'}x ${sync.itemName || 'Item'} (total: ${sync.newQty ?? '?'})`, { duration: 4000 })
                break
              case 'exit_recorded':
                toast.success(`📤 Saída: ${sync.quantity ?? '?'}x ${sync.itemName || 'Item'}${sync.partial ? ' (parcial)' : ''}`, { duration: 4000 })
                break
              default:
                break
            }
          }
          // AGC: Refresh finances, inventory, machinery, and fields data
          window.dispatchEvent(new CustomEvent('refresh-finances'))
          window.dispatchEvent(new CustomEvent('refresh-inventory'))
          window.dispatchEvent(new CustomEvent('refresh-machines'))
          window.dispatchEvent(new CustomEvent('refresh-fields'))
        } else {
          const errText = await response.text()
          console.error(`AGC: Failed to create ${entryType}:`, errText)
          toast.error(`Erro ao registrar ${entryType === 'revenue' ? 'receita' : 'despesa'}. Tente novamente.`)
        }
      } catch (err) {
        console.error(`AGC: Error creating ${entryType}:`, err)
        toast.error(`Erro ao registrar ${entryType === 'revenue' ? 'receita' : 'despesa'}. Tente novamente.`)
      }
      setAccumulatedFieldValues({})
      // AGC: Navigate to finances page
      setActivePage('finances')
      setActiveNav('home')
      handleVoiceModalClose()
      isSubmittingRef.current = false
      return
    }

    // AGC: Reset submission guard so subsequent voice entries can be submitted
    isSubmittingRef.current = false
  }, [lastResponse, user?.uid, handleVoiceModalClose])

  // AGC: List of pages that should hide TopNav and BottomNav
  const fullScreenPages: ActivePage[] = [
    'profile', 
    'notifications',
    'new-plot-1',
    'new-plot-2',
    'geometry-method',
    'geometry-gps',
    'geometry-manual',
    'crop-suggestions',
    'field-task-planning',
    'plot-summary',
    'polygon-map', // AGC: Polygon drawing is full screen (Junior)
    // AGC: 'plot-detail' removed from fullScreenPages - now renders inside MainMenu with TopNav/BottomNav
    'novo-lote', // AGC: Novo lote has its own header (Junior)
    'create-with-gps', // AGC: GPS creation is full screen (Junior)
    'ai-one-click-lot', // AGC: AI One-Click lot detection is full screen
    'auto-detect-fields', // AGC: Auto-detect fields via NDVI is full screen
    'finalizar-lote', // AGC: Finalize lot with name + crop
    'select-crop', // AGC: Crop selection has its own header (Junior)
    'management-plan', // AGC: Management plan has its own header (Junior)
    'lot-task-detail', // AGC: Lot task detail has its own header (Junior)
    'lot-page', // AGC: Lot Page has its own header and BottomNav
    'lot-images', // AGC: Lot Images page (PRO feature) has its own header
    'task-detail', // AGC: Task detail/edit page has its own header and footer
    'expenses', // AGC: Expenses module (Arturo)
    'new-expense', // AGC: New expense form (Arturo)
    // AGC: 'finances' removed - keep BottomNav visible on finances page
    'new-revenue', // AGC: New revenue form
    // AGC: 'inventory' removed — uses MainMenu's shared BottomNav (no page should render its own)
    'sistema-settings', // AGC: System config (Settings > Segurança > Sistema)
    'categorias-settings', // AGC: Categories management (Settings > Sistema > Categorias)
    'tarefas-settings', // AGC: Task types management (Settings > Sistema > Tipos de Tarefas)
    'maquinario-settings', // AGC: Machinery types management (Settings > Sistema > Tipos de Maquinário)
    'culturas-settings', // AGC: Crop types management (Settings > Sistema > Tipos de Culturas)
  ]

  // AGC: Render center content based on active page
  const renderCenterContent = () => {
    const { selectedFieldId, selectedTaskId, plotData, plotId, lotName, cropType, cropName, task } = navContext

    switch (activePage) {
      // AGC: Home/Dashboard
      case 'dashboard':
        return <Home onNavigate={handleScreenNavigate} />

      // AGC: Profile Page
      case 'profile':
        return (
          <Profile
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Notifications Page
      case 'notifications':
        return (
          <Notifications
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Lotes Module (NEW FLOW)
      case 'lotes-list':
        // AGC: Check if user has permission to view fields
        if (!permissions.canViewFields) {
          return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('profile.accessDenied.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('profile.accessDenied.fieldsDenied')}
              </p>
              <button
                onClick={() => setActivePage('dashboard')}
                className="mt-4 px-5 py-2.5 bg-navy-700 text-white rounded-full hover:bg-navy-800 transition-colors font-medium"
              >
                {t('profile.accessDenied.backToHome')}
              </button>
            </div>
          )
        }
        return <LotesList onNavigate={handleScreenNavigate} />
      
      case 'novo-lote':
        // AGC: Operador cannot create lots
        if (!permissions.canEditFields) {
          goBack()
          return null
        }
        return (
          <NovoLote
            onNavigate={handleScreenNavigate}
            onBack={goBack}
            initialName={navContext.lotName}
            initialFarmId={navContext.selectedFieldId || navContext.fieldId}
            voicePrefillArea={navContext.voicePrefillArea}
            voicePrefillCrop={navContext.voicePrefillCrop}
          />
        )
      
      case 'create-with-gps':
        return (
          <CreateWithGPS
            lotName={navContext.lotName || lotName || 'Novo lote'}
            onCancel={goBack}
            onSave={(data) => {
              console.log('AGC: GPS lot saved:', data)
              
              // AGC: Navigate to crop suggestions with plot data
              // AGC: Use lotName and fieldId from NovoLote (phase 1 selection)
              const generatedId = `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
              const plotFormData: PlotFormData = {
                id: generatedId,
                name: navContext.lotName || data.name || 'Novo lote',
                area: data.area,
                cropType: '' as any,
                coordinates: data.coordinates,
                fieldId: navContext.selectedFieldId || navContext.fieldId,
              }
              
              console.log('🆔 AGC: GPS flow - ID:', generatedId, 'Farm:', plotFormData.fieldId)
              navigateToPage('crop-suggestions', { plotData: plotFormData })
            }}
          />
        )
      
      // AGC: AI One-Click Lot Detection Screen
      case 'ai-one-click-lot':
        return (
          <AIOneClickLot
            onNavigate={handleScreenNavigate}
            onBack={goBack}
            initialLotName={navContext.lotName}
          />
        )
      
      // AGC: Auto-Detect Fields via NDVI Satellite Screen
      case 'auto-detect-fields':
        return (
          <AutoDetectFields
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      // AGC: Finalizar Lote - Confirm lot info after AI/auto detection
      // AGC: Now receives pre-selected farm + name from NovoLote (phase 1)
      case 'finalizar-lote':
        return (
          <FinalizarLote
            onNavigate={handleScreenNavigate}
            onBack={goBack}
            coordinates={navContext.coordinates}
            metrics={navContext.metrics}
            detectionMethod={navContext.detectionMethod}
            // AGC: Pre-filled name from NovoLote phase 1 or voice command
            initialName={navContext.lotName}
            // AGC: Pre-selected farm from NovoLote phase 1
            initialFarmId={navContext.selectedFieldId || navContext.fieldId}
            voicePrefillArea={navContext.voicePrefillArea}
            voicePrefillCrop={navContext.voicePrefillCrop}
          />
        )
      
      case 'polygon-map':
        return (
          <PolygonDrawingMap
            lotName={navContext.lotName || lotName || 'Novo lote'}
            onCancel={goBack}
            onSave={(polygon) => {
              // AGC: Navigate to crop suggestions with plot data
              // AGC: Use lotName and fieldId from NovoLote (phase 1 selection)
              const generatedId = `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
              
              const plotFormData: PlotFormData = {
                id: generatedId,
                name: navContext.lotName || polygon.name || lotName || 'Novo lote',
                area: polygon.area,
                cropType: '' as any,
                coordinates: polygon.coordinates,
                fieldId: navContext.selectedFieldId || navContext.fieldId,
              }
              
              console.log('🆔 AGC: Polygon flow - ID:', generatedId, 'Farm:', plotFormData.fieldId)
              navigateToPage('crop-suggestions', { plotData: plotFormData })
            }}
          />
        )
      
      case 'plot-detail':
        return (
          <LoteDetail
            plotId={plotId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      // AGC: Lot Page - Main lot monitoring page
      case 'lot-page':
        return (
          <LotPage
            plotId={plotId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      // AGC: Lot Images - Satellite imagery for a plot (PRO feature)
      case 'lot-images':
        return (
          <PlotImagesPage
            plotId={plotId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      // AGC: Farm Lots Map View - Map showing all lots of current farm
      case 'farm-lots-map':
        return (
          <FarmLotsMapView
            farmId={navContext.farmId}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      // AGC: Task management flow
      case 'select-crop':
        return (
          <SelectCrop
            plotId={plotId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      case 'management-plan':
        return (
          <ManagementPlan
            plotId={plotId || ''}
            cropType={cropType as any || 'soja'}
            cropName={cropName || 'Soja'}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      case 'lot-task-detail':
        // AGC: MIGRATED - Now uses canonical TaskDetail component
        // AGC: 'lot-task-detail' redirects to TaskDetail with task ID from navContext
        return (
          <TaskDetail
            taskId={task?.id || selectedTaskId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Fields Module (LEGACY - kept for compatibility)
      case 'fields':
        return <FieldsList onNavigate={handleScreenNavigate} />
      
      case 'field-detail':
        return (
          <FieldDetail
            fieldId={selectedFieldId || ''}
            onNavigate={handleScreenNavigate}
          />
        )
      
      // AGC: New field creation flow - Step 1 (Name only)
      case 'new-plot-1':
        return (
          <NewPlotStep1
            initialData={plotData}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      // AGC: Step 2 - Choose geometry method (GPS or Manual)
      case 'geometry-method':
        return (
          <GeometryMethodSelection
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      // AGC: Step 3a - GPS path geometry (simulation)
      case 'geometry-gps':
        return (
          <GPSPathGeometry
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      // AGC: Step 3b - Manual polygon drawing
      case 'geometry-manual':
        return (
          <ManualPolygonGeometry
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      // AGC: Step 4 - Crop suggestions
      case 'crop-suggestions':
        return (
          <CropSuggestions
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
            // AGC: Voice pre-filled crop from voice command
            voicePrefillCrop={navContext.voicePrefillCrop}
          />
        )
      
      // AGC: Step 5 - Task planning for the field
      case 'field-task-planning':
        return (
          <FieldTaskPlanning
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      // AGC: Legacy - Original step 2 (map drawing - kept for backwards compatibility)
      case 'new-plot-2':
        return (
          <NewPlotStep2
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      case 'plot-summary':
        return (
          <PlotSummary
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Tasks Module
      case 'tasks':
        // AGC: Check if user has permission to view tasks
        if (!permissions.canViewTasks) {
          return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('profile.accessDenied.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('profile.accessDenied.tasksDenied')}
              </p>
              <button
                onClick={() => setActivePage('dashboard')}
                className="mt-4 px-5 py-2.5 bg-navy-700 text-white rounded-full hover:bg-navy-800 transition-colors font-medium"
              >
                {t('profile.accessDenied.backToHome')}
              </button>
            </div>
          )
        }
        // AGC: Pass showOnlyOwnTasks prop for 'operador' subtype
        return <TasksList onNavigate={handleScreenNavigate} showOnlyOwnTasks={!permissions.canViewAllTasks} userId={user?.uid} />
      
      case 'new-task':
        // AGC: Operador cannot create tasks
        if (!permissions.canEditTasks) {
          goBack()
          return null
        }
        return (
          <NewTask
            onNavigate={handleScreenNavigate}
            onBack={goBack}
            preselectedPlotId={navContext.preselectedPlotId}
          />
        )
      
      case 'task-detail':
        return (
          <TaskDetail
            taskId={selectedTaskId || ''}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Weather Module
      case 'weather':
        return <WeatherDashboard onNavigate={handleScreenNavigate} />
      
      case 'spray-suitability':
        return (
          <SpraySuitability
            onNavigate={handleScreenNavigate}
            onBack={goBack}
            weatherCoordinates={navContext.weatherCoordinates}
          />
        )
      
      case 'forecast':
        return (
          <Forecast
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Expenses/Despesas Module - Requires financial permission
      case 'expenses':
        // AGC: Check if user has permission to view financial data
        if (!permissions.canViewFinancial) {
          return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('profile.accessDenied.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('profile.accessDenied.financialDenied')}
              </p>
              <button
                onClick={() => setActivePage('dashboard')}
                className="mt-4 px-5 py-2.5 bg-navy-700 text-white rounded-full hover:bg-navy-800 transition-colors font-medium"
              >
                {t('profile.accessDenied.backToHome')}
              </button>
            </div>
          )
        }
        return (
          <Despesas
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      case 'new-expense':
        // AGC: Check if user has permission to edit financial data
        if (!permissions.canEditFinancial) {
          goBack()
          return null
        }
        return (
          <NewExpense
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Unified Finances Page — Expenses + Revenues + Summary
      case 'finances':
        // AGC: Check if user has permission to view financial data
        if (!permissions.canViewFinancial) {
          return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('profile.accessDenied.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('profile.accessDenied.financialDenied')}
              </p>
              <button
                onClick={() => setActivePage('dashboard')}
                className="mt-4 px-5 py-2.5 bg-navy-700 text-white rounded-full hover:bg-navy-800 transition-colors font-medium"
              >
                {t('profile.accessDenied.backToHome')}
              </button>
            </div>
          )
        }
        return (
          <FinancesPage
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: New Revenue Form
      case 'new-revenue':
        // AGC: Check if user has permission to edit financial data
        if (!permissions.canEditFinancial) {
          goBack()
          return null
        }
        return (
          <NewRevenue
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Subscription Management
      case 'subscription':
        return (
          <SubscriptionPage
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Human Advisor Marketplace (Premium only)
      case 'advisor-marketplace':
        return (
          <AdvisorMarketplace
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      case 'consultant-profile':
        return (
          <ConsultantProfile
            consultantId={navContext.selectedFieldId}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      case 'consultation-request':
        return (
          <ConsultationRequest
            consultantId={navContext.selectedFieldId}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      case 'consultations':
        return (
          <ConsultationsList
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )
      
      case 'consultation-detail':
        return (
          <ConsultationDetail
            consultationId={navContext.selectedFieldId}
            onNavigate={handleScreenNavigate}
            onBack={goBack}
          />
        )

      // AGC: Unified Inventory Page - combines stock management and task requirements
      case 'inventory':
      case 'inputs': // AGC: Redirect to unified inventory page
        return (
          <UnifiedInventoryPage
            onBack={goBack}
            onNavigate={handleScreenNavigate}
            autoOpenScanModal={navContext.autoOpenScanModal}
          />
        )

      // AGC: NEW - Machinery Page (Visual Refresh)
      case 'machinery':
        return (
          <MachinesList
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      case 'machine-form':
        return (
          <AddMachine
            onBack={goBack}
            onNavigate={handleScreenNavigate}
            machineId={navContext.machineId}  // AGC: Pass machineId for edit mode
          />
        )
      
      // AGC: Machine Detail Page
      case 'machine-detail':
        return (
          <MachineDetail
            machineId={navContext.machineId || navContext.selectedFieldId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Settings Page
      case 'settings':
        return (
          <Settings
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Sistema settings (Settings > Segurança > Sistema)
      case 'sistema-settings':
        return (
          <SistemaSettings
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Categorias settings (Settings > Sistema > Categorias)
      case 'categorias-settings':
        return (
          <CategoriasSettings
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Tarefas (task types) settings (Settings > Sistema > Tipos de Tarefas)
      case 'tarefas-settings':
        return (
          <TarefasSettings
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Maquinário (machinery types) settings (Settings > Sistema > Tipos de Maquinário)
      case 'maquinario-settings':
        return (
          <MaquinarioSettings
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Culturas (crop types) settings (Settings > Sistema > Tipos de Culturas)
      case 'culturas-settings':
        return (
          <CulturasSettings
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Voice Calibration wizard (Settings > Sistema > Calibração de Voz)
      case 'voice-calibration':
        return (
          <VoiceCalibration
            onComplete={() => handleScreenNavigate('sistema-settings')}
            onBack={goBack}
          />
        )

      // AGC: NEW - Team Management Page (consolidated: users, roles, permissions)
      case 'team-users':
        // AGC: Gate on canViewTeam so any role with team visibility (owner/manager/agronomist/financial/worker/viewer) can open the page.
        // AGC: Add/remove/role-change actions inside the page are gated separately by their specific FarmPermission.
        if (!rbac.permissions?.canViewTeam) {
          return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
              <div className="w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('profile.accessDenied.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('profile.accessDenied.teamDenied')}
              </p>
              <button
                onClick={() => setActivePage('dashboard')}
                className="mt-4 px-5 py-2.5 bg-navy-700 text-white rounded-full hover:bg-navy-800 transition-colors font-medium"
              >
                {t('profile.accessDenied.backToHome')}
              </button>
            </div>
          )
        }
        return (
          <TeamUsers
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      case 'team-add-member':
        // AGC: Adding members requires canInviteUser (owner + manager only)
        if (!rbac.permissions?.canInviteUser) {
          goBack()
          return null
        }
        return (
          <AddTeamMember
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )
      
      // AGC: NEW - Member Profile Page
      case 'member-profile':
        return (
          <MemberProfile
            memberId={navContext.memberId || ''}
            onBack={goBack}
            onNavigate={handleScreenNavigate}
          />
        )

      // AGC: Default fallback
      default:
        return <Home onNavigate={handleScreenNavigate} />
    }
  }

  // AGC: TopNav REMOVED - Replaced by hamburger + drawer
  // AGC: Check if current page should show BottomNav
  const showBottomNav = !fullScreenPages.includes(activePage)

  // AGC: Debug log for navigation
  useEffect(() => {
    console.log('🔍 AGC: MainMenu - activePage:', activePage, 'showBottomNav:', showBottomNav, 'isDrawerOpen:', isDrawerOpen)
    console.log('🔍 AGC: fullScreenPages includes field-detail?', fullScreenPages.includes('field-detail'))
    if (activePage === 'field-detail') {
      console.log('✅ AGC: FieldDetail should render with BottomNav visible!')
    }
  }, [activePage, showBottomNav, isDrawerOpen])

  // AGC: contentOnly mode — render just the center content + voice modals (shell provided by MainLayout)
  if (contentOnly) {
    return (
      <>
        {renderCenterContent()}
        <VoiceCommandHelpModal
          isVisible={showVoiceHelpModal}
          onClose={handleVoiceHelpClose}
          onPreferenceChoose={handleVoiceHelpPreference}
        />
        {/* AGC: VoiceRecordingBar removed from contentOnly — MainLayout renders the bar
            and owns the recording hook. Having a second bar here caused a dual-hook bug
            where tapping Send called MainMenu's hook (idle) instead of MainLayout's (recording). */}
        <VoiceRecordingModal
          processingOnly
          isVisible={isVoiceModalVisible}
          state={voiceState}
          progress={progress}
          duration={duration}
          error={voiceError}
          lastResponse={lastResponse}
          onClose={handleVoiceModalClose}
          onStopRecording={handleStopRecording}
          onCancelRecording={handleCancelRecording}
          onSilentCancel={handleCancelRecording}
          onFieldSuggestionSelect={handleFieldSuggestionSelect}
          onConfirmFields={handleConfirmFields}
          voiceMode={voiceMode}
          isOnline={isVoiceOnline}
          queueCount={voiceQueueCount}
        />
      </>
    )
  }

  return (
    <>
      {isDesktopLayout ? (
        // AGC: Desktop browser shell — CSS grid: sidebar column + content column (header + scrollable main)
        // AGC: Never activated on Capacitor native (useIsDesktopLayout guards against it)
        <div className="h-screen w-full grid grid-cols-[240px_minmax(0,1fr)] bg-[#fafbfc] dark:bg-gray-900 transition-colors overflow-hidden">
          <DesktopSidebar
            active={activeNav}
            onNavigate={handleNavClick}
            onNavigateScreen={navigateToPage}
            activeScreen={activePage as Screen}
            onMicPress={handleMicPress}
            onMicLongPress={handleMicLongPress}
            onMicLongPressEnd={handleMicLongPressEnd}
            onMenuClick={() => setIsDrawerOpen(true)}
            onNotificationsClick={() => navigateToPage('notifications')}
            notificationCount={unreadCount}
          />

          {/* AGC: Content column — no topnav on desktop (hamburger + bell are in DesktopSidebar) */}
          <div className="flex flex-col h-screen overflow-hidden">
            <main className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className={`${getDesktopWidthClass(activePage)} mx-auto px-6 py-6`}>
                {renderCenterContent()}
              </div>
            </main>
          </div>

          {/* AGC: AppDrawer overlays only the content area (left: 240px) so sidebar stays interactive */}
          <AppDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            currentScreen={activePage as Screen}
            onNavigate={navigateToPage}
            userName={userName}
            userAvatar={userAvatar}
            userEmail={user?.email}
            appVersion={APP_VERSION}
            isOnline={navigator.onLine}
            desktopOffsetLeft={240}
          />
        </div>
      ) : (
        // AGC: Mobile shell — UNCHANGED from pre-2.13.0 (byte-for-byte identical JSX)
        // AGC: This branch is what Capacitor iOS/Android always receives
        <div
          className="min-h-screen w-full flex flex-col transition-colors bg-[#fafbfc] dark:bg-gray-900"
          style={{
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          {/* AGC: NEW - Global App Header (hamburger + brand + notifications) */}
          <AppHeader
            onMenuClick={() => setIsDrawerOpen(true)}
            onNotificationsClick={() => navigateToPage('notifications')}
            notificationCount={unreadCount}
          />

          {/* AGC: AppDrawer (left side navigation) */}
          <AppDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            currentScreen={activePage as Screen}
            onNavigate={navigateToPage}
            userName={userName}
            userAvatar={userAvatar}
            userEmail={user?.email}
            appVersion={APP_VERSION}
            isOnline={navigator.onLine}
          />

          {/* AGC: CENTER SECTION - Dynamic page content */}
          <main
            className={`flex-1 overflow-y-auto overflow-x-hidden ${showBottomNav ? 'pb-24' : ''}`}
            style={{
              // AGC: Edge-to-edge is disabled at the Android native layer, so the WebView
              // AGC: is already inset above the system nav bar. Just keep a small visual gap.
              paddingBottom: showBottomNav
                ? undefined
                : 'calc(max(env(safe-area-inset-bottom), 10px) + 10px)',
            }}
          >
            {renderCenterContent()}
          </main>

          {/* AGC: BOTTOM SECTION - Fixed bottom navigation */}
          {showBottomNav && (
            <BottomNav
              active={activeNav}
              onNavigate={handleNavClick}
              onMicPress={handleMicPress}
              onMicLongPress={handleMicLongPress}
              onMicLongPressEnd={handleMicLongPressEnd}
              onMicSlideCancel={handleBarSlideCancel}
              isRecording={recordingMode !== 'idle'}
              recordingMode={recordingMode}
            />
          )}
          {/* AGC: I4 — AnimatePresence enables exit animation when recording bar unmounts */}
          <AnimatePresence>
            {recordingMode !== 'idle' && (
              <VoiceRecordingBar
                key="recording-bar"
                mode={recordingMode as 'tap' | 'hold'}
                duration={duration}
                isRecording={isRecording}
                isPaused={isPaused}
                analyserNode={analyserNode}
                onSend={handleBarSend}
                onCancel={handleBarCancel}
                onPause={pauseRecording}
                onResume={resumeRecording}
                onSlideCancel={handleBarSlideCancel}
                onRelease={handleBarRelease}
              />
            )}
          </AnimatePresence>
        </div>
      )}

      {/* AGC: Modals stay outside the shell branch — they overlay both desktop and mobile */}
      {/* AGC: First-time / weekly voice command help (transparent overlay with examples) */}
      <VoiceCommandHelpModal
        isVisible={showVoiceHelpModal}
        onClose={handleVoiceHelpClose}
        onPreferenceChoose={handleVoiceHelpPreference}
      />

      {/* AGC: Voice Recording Modal Overlay — processingOnly since bar handles recording UI */}
      <VoiceRecordingModal
        processingOnly
        isVisible={isVoiceModalVisible}
        state={voiceState}
        progress={progress}
        duration={duration}
        error={voiceError}
        lastResponse={lastResponse}
        onClose={handleVoiceModalClose}
        onStopRecording={handleStopRecording}
        onCancelRecording={handleCancelRecording}
        onSilentCancel={handleCancelRecording}
        onFieldSuggestionSelect={handleFieldSuggestionSelect}
        onConfirmFields={handleConfirmFields}
        voiceMode={voiceMode}
        isOnline={isVoiceOnline}
        queueCount={voiceQueueCount}
      />
    </>
  )
}

export default MainMenu
